import React from 'react';
import { cn } from '../lib/utils';

/**
 * El estado de seguimiento de un paciente, como etiqueta.
 *
 * EL COLOR ACÁ INFORMA, NO DECORA, así que es la excepción declarada a la regla
 * de que todo color sale de la marca. Un semáforo pintado con el naranja del
 * negocio no distingue "al día" de "en riesgo", que es lo único que tiene que
 * hacer. Es el mismo criterio del verde de PasoPlegable y del semáforo de
 * CurrentPlan.
 *
 * Y NUNCA COLOR SOLO. Cada estado lleva su palabra escrita al lado del punto.
 * Una lista donde el único dato es un círculo de color deja afuera a quien no
 * distingue rojo de verde —que es cerca del 8% de los varones—, y encima se
 * vuelve ilegible impresa o en blanco y negro. El punto es refuerzo del texto,
 * no al revés.
 *
 * Los textos no son los ids: `en-riesgo` es una clave, "En riesgo" es lo que se
 * lee. Y el de `sin-datos` dice "Sin actividad" en vez de "sin datos", porque
 * describe a la persona y no al estado de la base.
 */

const ESTILOS = {
  'en-riesgo': { texto: 'En riesgo', punto: 'bg-red-500', caja: 'border-red-200 bg-red-50 text-red-700' },
  atrasado: { texto: 'Atrasado', punto: 'bg-amber-500', caja: 'border-amber-200 bg-amber-50 text-amber-800' },
  'al-dia': { texto: 'Al día', punto: 'bg-emerald-500', caja: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  pausado: { texto: 'Pausado', punto: 'bg-neutral-400', caja: 'border-neutral-200 bg-neutral-50 text-neutral-600' },
  'sin-datos': { texto: 'Sin actividad', punto: 'bg-neutral-300', caja: 'border-neutral-200 bg-neutral-50 text-neutral-500' },
};

/**
 * Desde cuándo no aparece, en palabras.
 *
 * "Nunca contestó" y "hace 20 días" son cosas distintas que el estado solo no
 * distingue: las dos pueden ser "en riesgo", pero una es alguien que se fue y la
 * otra alguien que no arrancó nunca, y se llaman distinto.
 */
export function desdeCuando({ diasSinCheckin, nuncaHizoCheckin }) {
  if (nuncaHizoCheckin) return 'Nunca hizo un check-in';
  if (diasSinCheckin === null || diasSinCheckin === undefined) return '';
  if (diasSinCheckin === 0) return 'Contestó hoy';
  if (diasSinCheckin === 1) return 'Hace 1 día';
  return `Hace ${diasSinCheckin} días`;
}

export default function SemaforoDePaciente({ estado, compacto = false, className }) {
  const e = ESTILOS[estado] || ESTILOS['sin-datos'];

  if (compacto) {
    // En la lista, donde el ancho es poco: el punto con el texto como título
    // accesible. Sigue sin ser color solo — un lector de pantalla lo lee, y el
    // renglón de abajo dice hace cuánto.
    return (
      <span className={cn('inline-flex items-center gap-1.5', className)}>
        <span className={cn('h-2 w-2 shrink-0 rounded-full', e.punto)} aria-hidden="true" />
        <span className="text-[0.7rem] font-medium text-neutral-600">{e.texto}</span>
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[0.72rem] font-semibold',
        e.caja,
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', e.punto)} aria-hidden="true" />
      {e.texto}
    </span>
  );
}
